
import { LatLng, WeatherData } from "../types";
import { getCyberLocationName } from "./locationService";

// Open-Meteo WMO Weather interpretation codes
const getWeatherCondition = (code: number, lang: 'en' | 'ar'): string => {
  const codes: Record<number, { en: string; ar: string }> = {
    0: { en: 'Clear Sky', ar: 'سماء صافية' },
    1: { en: 'Mainly Clear', ar: 'غائم جزئياً' },
    2: { en: 'Partly Cloudy', ar: 'غائم جزئياً' },
    3: { en: 'Overcast', ar: 'غائم كلياً' },
    45: { en: 'Fog', ar: 'ضباب' },
    48: { en: 'Depositing Rime Fog', ar: 'ضباب جليدي' },
    51: { en: 'Light Drizzle', ar: 'رذاذ خفيف' },
    53: { en: 'Moderate Drizzle', ar: 'رذاذ معتدل' },
    55: { en: 'Dense Drizzle', ar: 'رذاذ كثيف' },
    61: { en: 'Slight Rain', ar: 'مطر خفيف' },
    63: { en: 'Moderate Rain', ar: 'مطر معتدل' },
    65: { en: 'Heavy Rain', ar: 'مطر غزير' },
    71: { en: 'Slight Snow', ar: 'ثلج خفيف' },
    73: { en: 'Moderate Snow', ar: 'ثلج معتدل' },
    75: { en: 'Heavy Snow', ar: 'ثلج كثيف' },
    95: { en: 'Thunderstorm', ar: 'عاصفة رعدية' },
  };

  return codes[code] ? codes[code][lang] : (lang === 'ar' ? 'غير معروف' : 'Unknown');
};

export const getWeather = async (location: LatLng, lang: 'en' | 'ar'): Promise<WeatherData> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000); // 8s timeout

    // FETCH HOURLY DATA for AI Summary
    const response = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${location.lat}&longitude=${location.lng}&current_weather=true&hourly=temperature_2m,precipitation,weathercode,windspeed_10m,relativehumidity_2m&forecast_days=1`,
      { signal: controller.signal }
    );
    clearTimeout(timeoutId);
    
    if (!response.ok) throw new Error('Weather API failed');
    
    const data = await response.json();
    const current = data.current_weather;
    const hourIndex = new Date().getHours();
    
    // Safety check for hourly data
    const humidity = data.hourly?.relativehumidity_2m?.[hourIndex] || 50;

    const isNight = current.is_day === 0;
    const locationName = await getCyberLocationName(location.lat, location.lng, lang);

    return {
      temp: Math.round(current.temperature),
      condition: getWeatherCondition(current.weathercode, lang),
      humidity: humidity,
      windSpeed: Math.round(current.windspeed),
      locationName: locationName,
      isNight,
      hourly: data.hourly // PASSING RAW DATA TO AI
    };
  } catch (error) {
    console.warn("Weather Service Error:", error);
    return {
      temp: 0,
      condition: 'OFFLINE',
      humidity: 0,
      windSpeed: 0,
      locationName: 'Unknown Sector',
      isNight: true
    };
  }
};
